/**
 * @file This file contains the configuration for the game's feature list,
 * displayed on the guide page.
 * @module featuresConfig
 */

export const featuresConfig = [
    {
        title: 'Campaign Mode',
        status: 'in-progress',
        items: [
            { text: 'Progression: Players complete all biomes on the "Simple" path, then "Default", and finally "Long".', status: 'done' },
            { text: 'Difficulty Ramp: Animals do not appear on "Simple" levels to provide an easier introduction.', status: 'done' },
            {
                text: 'Milestone Rewards: Award a bonus (e.g., 💰 Gold) for reaching specific upgrade path milestones.', status: 'done', subItems: [
                    { text: 'The Gold total is displayed next to the score in the UI.', status: 'done' }
                ]
            },
            { text: 'Level Goals: Each level could have unique goals, e.g., "Survive 50 turns," or "Rescue 5 foxes."', status: 'todo' },
            { text: 'Stash Feature: A small inventory where players can store an item to use later in the same level.', status: 'todo' },
            { text: 'Achievements: A star rating system (e.g., Gold, Silver, Bronze) for level completion.', status: 'todo' },
        ]
    },
    {
        title: 'Quick Game Modes',
        status: 'in-progress',
        items: [
            { text: 'Open Mode: The default game mode is an open-ended "play until you lose".', status: 'done' },
            { text: 'Limited Turns: Challenge for the highest score within a set number of turns.', status: 'done' },
            { text: 'Crafting Mode: An alternative mode where items are created by combining ingredients based on recipes.', status: 'done' },
            { text: 'Game Over: The game ends when no more moves can be made (grid is full).', status: 'done' },
            { text: 'Target Score: A race to reach a specific score as quickly as possible.', status: 'todo' },
            {
                text: 'Rolling Grid Mode: A mode where items continuously fall, and the player controls a collector at the bottom to catch and arrange them.',
                status: 'in-progress',
                subItems: [
                    { text: 'On load, a portion of the grid is pre-filled with random gems.', status: 'done' },
                    { text: 'Generally every row of the grid should have some gems filled in. Overall it should be quite balanced, not all towards the bottom of the grid.', status: 'todo' },
                    { text: 'The collector bucket moves only 1 cell width at a time. It is not a slider.'},
                    { text: 'A turn is taken when the bucket shifts left or right one cell. Or when the user stays still, by pressing the downward arrow key.'},
                    { text: 'Each turn, all items on the grid "fall" down one row.', status: 'done' },
                    { text: 'A new, partially filled row of random gems is added to the top each turn.', status: 'done' },
                    { text: 'A player-controlled "bucket" at the bottom moves left/right to catch falling items.', status: 'done' },
                    { text: 'The bucket can stack up to 5 items.', status: 'done' },
                    {text: 'the bucket can only catch one item at a time, and it is the item which is directly above the bucket when an item falls.', status: 'done'},
                    {text: 'Any items outside of the 1 cell catching area will fall away and disappear.', status: 'done'},
                    {text: 'The bucket should be oriented vertically. The items that fall into the bucket should fill from the bottom up.', status: 'done'},
                    {text: 'If a turn is taken and the bucket is filled, when the items fall, the lowest item in the bucket (the oldest one) should fall out of the bottom.'},
                    { text: 'If 3 or more consecutive items in the bucket match (are identical), they merge into a the next upgraded item.', status: 'done' },
                    {text:'items should only fall from the playable grid area, the buffer is not involved in this game mode.', status: 'done'}
                ]
            }
        ]
    },
    {
        title: 'Gameplay & Interactive Features',
        status: 'in-progress',
        items: [
            { text: 'Core Merge Mechanic: Match 3 or more identical items to upgrade them.', status: 'done' },
            { text: 'Mega Merges: Matching a large group of items (e.g., 9+) provides a tier bonus.', status: 'done' },
            { text: 'Item Queue: Players can see the next items to be placed, allowing for strategic planning.', status: 'done' },
            { text: 'Bonus Items / Power-ups: Special items like a "Clean-up" bomb or a "Pickaxe" to remove obstacles.', status: 'done' },
            { text: 'NPCs (Human and Non-Human): Characters that move around the grid independently.', status: 'done' },
            {
                text: 'NPC Interactions:',
                status: 'done',
                subItems: [
                    { text: 'Speech bubbles appear when human NPCs are adjacent.', status: 'done' },
                    { text: 'Human NPCs randomly hold accessories (tools, food, etc.).', status: 'done' },
                    { text: 'NPCs bob up and down as they walk.', status: 'todo' },
                    { text: 'NPCs go into a pool of ex-inhabitants instead of "dying".', status: 'todo' },
                    { text: 'Ex-inhabitants have a chance to return instead of a new NPC spawning.', status: 'todo' },
                    { text: 'Ex-inhabitants remain visible in the NPC editor.', status: 'todo' },
                    { text: 'Each NPC should have an internal log of actions and visits.', status: 'todo' },
                    { text: 'Zany actions should be triggered by a global random timer, not just by individual chance.', status: 'todo' },
                    { text: 'NPCs can walk off-grid and respawn as a new character with a new accessory.', status: 'done' },
                    { text: 'When NPCs interact, they can randomly swap accessories.', status: 'done' },
                    { text: 'When an NPC gets a new destination, a temporary "signpost" appears on the target cell.', status: 'done' }

                ]
            },
            {
                text: 'NPC Respawning: NPCs can walk off-grid and respawn.', status: 'in-progress', subItems: [
                    { text: 'Human NPCs respawn as a new character with a new accessory.', status: 'done' },
                    { text: 'Non-human NPCs respawn as a new random non-human NPC.', status: 'done' }
                ]
            },
            { text: 'Random Disasters: Events like Godzilla, Whirlwind, and Storm can occur.', status: 'done' },
            { text: 'Weather Effects: Non-gameplay visual effects like rain, snow, sun, and fog.', status: 'done' },
            { text: 'Ambient Flying Animals: Similar to weather, non-interactive animals can fly across the screen on a timer.', status: 'Done' },
            { text: 'Day/Night Cycle: A visual cycle that won\'t affect gameplay (e.g., lighting).', status: 'Done' }
        ]
    },
    {
        title: 'World & Environment',
        status: 'done',
        items: [
            { text: 'Multiple Biomes: Different visual themes with unique upgrade paths.', status: 'done' },
            { text: 'Consistent Upgrade Path Lengths: Standardized path lengths for consistent difficulty.', status: 'done' },
            { text: 'Multiple Animal Types: Includes various animals that can be trapped.', status: 'done' },
            { text: 'Stationary Obstacles: Rocks and Water are randomly placed on the grid.', status: 'done' },
            {
                text: 'Grid Buffer Zone: A non-playable buffer area around the main grid for visual effects.',
                status: 'done',
                subItems: [
                    { text: 'Buffer tiles are decorated with biome-specific, randomly selected icons.', status: 'done' },
                    { text: 'Gems should look more like tiles.', status: 'todo' },
                    { text: 'Can the settlements tilt upwards separately from the background colour, and can they cast an accurate shadow?', status: 'todo' },
                    { text: 'Roads and pathways as an ambient overlay.', status: 'todo' },
                    { text: 'Walls or fences that NPCs have to pathfind around.', status: 'todo' }

                ]
            }
        ]
    },
    {
        title: 'Player Progression & Meta',
        status: 'in-progress',
        items: [
            { text: 'High Score Manager: Top 10 scores are saved locally.', status: 'done' },
            { text: 'Achievements & Player Stats: Tracks various stats and unlocks achievements with toast notifications.', status: 'done' },
            { text: 'Gold System: Players earn gold from milestones.', status: 'done' },
            {
                text: 'Save & Continue: Allows the player to save and resume their game.',
                status: 'done',
                subItems: [
                    { text: 'Handles separate Quick Game and Campaign saves.', status: 'done' }
                ]
            },
            {text: 'Autosave Feature',
                status: 'done',
                subItems: [
                    { text: 'Game autosaves every 10 turns.', status: 'done' },
                    { text: 'On load, prompt user to restore from autosave if available.', status: 'done' }
                ]
            }
        ]
    },
    {
        title: 'User Guidance & Onboarding',
        status: 'in-progress',
        items: [
            {
                text: 'Tutorial System: A step-by-step guide for new players.', status: 'done', subItems: [
                    { text: 'Event-driven steps that can wait for player actions.', status: 'done' },
                    { text: 'Only runs for first-time players based on player stats.', status: 'done' },
                    { text: 'Guides the player through placing an item and making their first merge.', status: 'done' },
                    { text: 'Tutorials can be skipped or disabled.', status: 'todo' }
                ]
            },
            {
                text: 'Tooltip System: Contextual callouts to explain UI elements.', status: 'done', subItems: [
                    { text: 'Can be attached to any UI element and positioned.', status: 'done' },
                    { text: 'Can be configured to show only once and be dismissed by the player.', status: 'done' }
                ]
            },
            { text: 'Banner System: Large, temporary announcements for important events (e.g., "Level Start").', status: 'done' },
            {
                text: 'Visual Cues:',
                status: 'done',
                subItems: [
                    { text: 'Screen Dimming: The background can be dimmed to highlight specific elements.', status: 'done' },
                    { text: 'Pointer Arrows: Animated arrows can point to specific UI elements.', status: 'done' }
                ]
            },
            {
                text: 'Odds Visualization: Displaying the probabilities of random events to the player.',
                status: 'todo',
                subItems: [
                    { text: 'Show the chance of the next item appearing in the queue.', status: 'done' },
                    { text: 'Display the likelihood of an animal or disaster event occurring.', status: 'todo' }
                ]
            }
            ,
            {
                text: 'Animal Spawn Forecast: Show an indicator in the item queue for when the next animal will spawn.',
                status: 'done'
            }
        ]
    },
    {
        title: 'UI & System',
        status: 'in-progress',
        items: [
            {
                text: 'Audio System: Sound effects for key actions and a global mute toggle.',
                status: 'in-progress',
                subItems: [
                    { text: 'Background music plays continuously, cycling through tracks.', status: 'Done' }

                ]
            },
            { text: 'Mobile Viewport / Responsive Design: Adapts the layout to be playable on mobile devices.', status: 'done' },
            { text: 'Settings Page: Allows viewing/deleting saved data and resetting player profiles.', status: 'done' },
            { text: 'Developer Tools: Includes a debug panel, cheat buttons, and test pages.', status: 'done' }
        ]
    },
    {
        title: 'Settlers Mode (New Game Mode Idea)',
        status: 'in-progress',
        items: [
            { text: 'A new game mode focused on building a town with a supply chain economy.', status: 'in-progress' },
            {
                text: 'Production Buildings: Players place buildings like Lumberjack Huts, Quarries, and Farms on the grid.',
                status: 'in-progress',
                subItems: [
                    { text: 'Buildings have recipes to convert input resources to output products.', status: 'done' }
                ]
            },
            {
                text: 'NPCs as Transporters: Instead of wandering randomly, NPCs are assigned jobs to transport resources.',
                status: 'in-progress',
                subItems: [
                    { text: 'NPCs have names and can be identified individually.', status: 'done' },
                    { text: 'NPCs intelligently seek out settlements that need their carried item.', status: 'done' },
                    { text: 'Empty-handed NPCs seek out settlements with finished products to distribute.', status: 'done' },
                    { text: 'When visiting a settlement, NPCs should shrink or fade to give a sense of entering the building.', status: 'todo' }
                ]
            },
            {
                text: 'Supply Chains: Create complex production chains (e.g., Wood -> Sawmill -> Planks).',
                status: 'in-progress'
            },
            { text: 'Goal: The objective is to build a thriving town by managing resources and production efficiently.', status: 'todo' },
            {
                text: 'NPC Skill System: Villagers gain experience and become more efficient at their jobs.',
                status: 'todo',
                subItems: [
                    { text: 'Experienced NPCs could move faster or have a higher chance of producing bonus items.', status: 'todo' }
                ]
            },
            {
                text: 'Settlement Upgrades: Players can upgrade production buildings.',
                status: 'todo',
                subItems: [
                    { text: 'Upgraded buildings could unlock more efficient recipes or increase inventory space.', status: 'todo' }
                ]
            },
            { text: 'Central Storage: Build a Warehouse or Stockpile where all goods are stored, rather than just at the production site.', status: 'todo' },
            { text: 'Marketplace & Orders: A central building where the player receives orders for specific goods (e.g., "Deliver 10 Bread"). Fulfilling orders yields rewards.', status: 'todo' },
            { text: 'Villager Needs & Happiness: NPCs consume resources like food and tools. Keeping them happy could provide production bonuses.', status: 'todo' }
        ]
    },
    {
        title: 'Sandbox / God Mode',
        status: 'todo',
        items: [
            { text: 'A freeplay mode where players can directly manipulate the game world.', status: 'todo' },
            {
                text: 'Floating UI Panel: A main panel for placing/editing items.', status: 'todo',
                subItems: [
                    { text: 'Add Panel: Select any item (NPC, settlement, resource) to place on the grid.', status: 'todo' },
                    { text: 'Edit Panel: Click an existing item to modify its properties (e.g., inventory, role).', status: 'todo' }
                ]
            },
            { text: 'Real-Time Strategy (RTS) Feel: Focus on building and managing the village without merge-puzzle constraints.', status: 'todo' },
            { text: 'Controllable NPC: Select an NPC to directly control, allowing the player to move them around the map and manage their inventory to move belongings between settlements.', status: 'todo' }
        ]
    },
    {
        title: 'Scenarios / Custom Maps',
        status: 'todo',
        items: [
            { text: 'Allow players to create and share their own pre-configured maps or scenarios.', status: 'todo' },
            {
                text: 'Scenario Editor: A tool for players to place items, settlements, and NPCs to design a starting state.', status: 'todo',
                subItems: [
                    { text: 'Define specific goals for the scenario (e.g., "Produce 50 bread within 100 turns").', status: 'todo' },
                    { text: 'Save and load scenarios from a local file or a central server.', status: 'todo' }
                ]
            },
            { text: 'Scenario Browser: An in-game browser to find and play scenarios created by other players.', status: 'todo' }
        ]
    },
    {
        title: 'NPC Lifecycle & Progression',
        status: 'todo',
        items: [
            {
                text: 'Initial Settlers: Start "Settlers Mode" with a specific family unit (2 adults, 2 babies).',
                status: 'done'
            },
            {
                text: 'NPC Aging Lifecycle: Implement a full aging process for villagers.',
                status: 'in-progress',
                subItems: [
                    { text: 'Babies age into Children (Boy/Girl).', status: 'done' },
                    { text: 'Children age into a random Adult NPC type.', status: 'done' },
                    { text: 'Adults age into Elders (new NPC type).', status: 'todo' },
                    { text: 'Add `isElder` flag to NPC configs.', status: 'todo' }
                ]
            },
            {
                text: 'Royalty & Immortality: Create a system for NPCs to become royalty.',
                status: 'in-progress',
                subItems: [
                    { text: 'An Elder that acquires a "Crown" accessory becomes a King/Prince/Princess.', status: 'done' },
                    { text: 'Royalty NPCs live forever and never leave the grid.', status: 'done' }
                ]
            },
            { text: 'NPC Reproduction: Adult NPCs who fall in love have a chance to spawn a new baby NPC.', status: 'done' },
            { text: 'Accessory Scarcity: Limit spawning accessories to only the most basic tools and resources.', status: 'in-progress' },
            { text: 'Food Production Chain: Create a clear "seed -> raw ingredient -> cooked meal" pipeline.', status: 'todo' },
            {
                text: 'Item Expiration & Food Spoilage: Differentiate between raw and cooked food.',
                status: 'todo',
                subItems: [
                    { text: 'Raw items (e.g., fish) could spoil over time, while processed goods (cooked fish) last longer.', status: 'todo' },
                    { text: 'This could be implemented by `type` (e.g., `raw_food`) rather than a parameter on each item.', status: 'todo' }
                ]
            }
        ]
    },
    {
        title: 'Bugs',
        status: 'in-progress',
        items: [
            { text: 'Powerups don\'t seem to be appearing in the queue.', status: 'done' }
        ]
    },
    {
        title: 'Code Quality & Principles',
        status: 'todo',
        items: [
            {
                text: 'Implement automated checks for SOLID and DRY principles.',
                status: 'todo'
            }
        ]
    },
    {
        title: 'Misc note',
        status: 'todo',
        items: [
            {
                text: 'NPC Skin tones',
                status: 'done'
            },
            {
                text: 'Stats for settlements',
                status: 'todo'
            },
            {
                text: 'Some other clickable NPC info panel so I can pin or select people manually',
                status: 'Done'
            },,
            {
                text: 'Some other clickable Settlement info panel so I can pin or select settlement manually',
                status: 'Done'
            },
            {
                text: 'An NPC editor. I would like to be able to select an NPC and set various values. For example, I might want to change their character manually, or name, or other toggles.',
                status: 'done'
            }
            ,
            {
                text: 'An Settlement editor. I would like to be able to select a Settlement and set various values. For example, I might want to change their character manually, or name, or other toggles.',
                status: 'Done'
            } ,
            {
                text: 'In SEttlement mode, I do not need to see score, gold, turns, next items, high scores. I will probably disable the whole queue. So it is purely Settlement mode and not merge game anymore in this mode.',
                status: 'todo'
            },
            {
                text: 'I need a max population. The screen is not nice to look at',
                status: 'done'

            },
            {
                text: 'I need to make Village Stats area. For example, number of settlements, number of NPC of the various ages and a total.',
                status: 'done'

            },
            {
                text: 'move UI above and below the game area in all modes',
                status: 'todo'

            }
            ,
            {
                text: 'If I click a settlement, I get a amanagementdialog. It should allow me to pause a settlement,for imports and/or exports. Do this first, then Feature 2.  allow or disallow imports and exports. It should allow me to tweak the recipes. It should give me a history of the last 10 things (X things) that happened, like last prodcution or last visitors.',
                status: 'todo'
            },
            {
                text: '"Mini Merge" Mode: A slot-machine style game mode.',
                status: 'in-progress',
                subItems: [
                    { text: 'Features a 1x3 grid where players manage a queue of 5 items.', status: 'done' },
                    { text: 'Players can "Hold" items they want to keep.', status: 'done' },
                    { text: 'A "Nudge" button replaces any un-held items with new ones from the queue.', status: 'done' },
                    { text: 'Matching three items in a row creates a merge.', status: 'done' },
                    { text: 'The newly merged item is automatically held for the next turn.', status: 'done' },
                    { text: 'This mode has its own menu entry, settings, and save slot.', status: 'done' },
                    { text: 'Animals, disasters, power-ups, and settlements are disabled.', status: 'done' },
                    { text: 'Win/loss conditions need to be defined.', status: 'todo' }
                ]
            }
        ]
    },
    {
        title: 'Spot the Odd Behaviour Mode (New Game Mode Idea)',
        status: 'todo',
        items: [
            { text: 'A new game mode where the player has to identify the NPC that is behaving differently from the others.', status: 'todo' }

        ]
    }
];